/**
 * Who somebody knows, and who they would rather not hear.
 *
 * Both lists live on the server against the character, so they follow whoever
 * is playing rather than the browser they happened to log in from, and so that
 * ignoring somebody actually silences them rather than merely hiding them: a
 * list the client enforced would be a list that stopped working the moment
 * anybody ran a client of their own.
 */

import { useState } from 'react';
import { FriendAct, type FriendActValue, type FriendsPacket } from '@inverse/protocol';

/** How many names either list holds, which the server enforces as well. */
const LIMIT = 100;

export function Friends({
  friends,
  ignored,
  asking,
  asked,
  onAct,
  onMessage,
  party,
  guild,
}: {
  friends: FriendsPacket['friends'];
  ignored: readonly string[];
  /** People who have asked to be friends, and people who have been asked. */
  asking: readonly string[];
  asked: readonly string[];
  onAct: (act: FriendActValue, name: string) => void;
  /** Opens the chat box addressed to them. */
  onMessage: (name: string) => void;
  /** The party panel, drawn on its own tab here because this is where people are. */
  party: JSX.Element;
  /** And the guild, for the same reason. */
  guild: JSX.Element;
}): JSX.Element {
  const [showing, setShowing] = useState<'friends' | 'ignored' | 'requests' | 'party' | 'guild'>(
    'friends',
  );
  const [typed, setTyped] = useState('');

  // Whoever is here comes first, since the reason for looking at this list at
  // all is nearly always to find somebody to talk to now.
  const sorted = [...friends].sort(
    (a, b) => Number(b.online) - Number(a.online) || a.name.localeCompare(b.name),
  );

  const here = sorted.filter((one) => one.online).length;
  const list =
    showing === 'friends'
      ? sorted.map((one) => one.name)
      : showing === 'ignored'
        ? [...ignored]
        : [...asking, ...asked];

  const add = (event: React.FormEvent): void => {
    event.preventDefault();
    const name = typed.trim();
    if (name.length === 0) return;

    // Asking on the requests tab as well as on the friends tab: both are the
    // same act, and somebody who has gone looking for the requests is the person
    // most likely to want to send one.
    onAct(showing === 'ignored' ? FriendAct.Ignore : FriendAct.AskFriend, name);
    setTyped('');
  };

  return (
    <div className="panel-body friends">
      <div className="friends-tabs">
        <button
          type="button"
          className={showing === 'friends' ? 'on' : ''}
          onClick={() => setShowing('friends')}
        >
          Friends <span className="quiet">{friends.length}</span>
        </button>
        <button
          type="button"
          className={showing === 'requests' ? 'on' : ''}
          onClick={() => setShowing('requests')}
        >
          Requests
          {/* Only when there is something to answer. A nought beside a tab is a
              thing to read and dismiss every time the panel is opened. */}
          {asking.length > 0 ? <span className="friends-waiting">{asking.length}</span> : null}
        </button>
        <button
          type="button"
          className={showing === 'party' ? 'on' : ''}
          onClick={() => setShowing('party')}
        >
          Party
        </button>
        <button
          type="button"
          className={showing === 'guild' ? 'on' : ''}
          onClick={() => setShowing('guild')}
        >
          Guild
        </button>
        <button
          type="button"
          className={showing === 'ignored' ? 'on' : ''}
          onClick={() => setShowing('ignored')}
        >
          Ignored <span className="quiet">{ignored.length}</span>
        </button>
      </div>

      {showing === 'party' ? party : null}
      {showing === 'guild' ? guild : null}

      {showing === 'friends' && friends.length > 0 ? (
        <p className="hint friends-count">
          {here} of {friends.length} here.
        </p>
      ) : null}

      {showing !== 'party' && showing !== 'guild' ? (
        <>
          <div className="friends-list">
            {showing === 'friends'
              ? sorted.map((one) => (
                  <div className={one.online ? 'friend on' : 'friend'} key={one.name}>
                    <span className="friend-dot" aria-hidden />
                    <span className="friend-name">{one.name}</span>

                    {/* Where they are, which is the area of the world they are
                      standing in and the same name their own map shows them. */}
                    {one.online && one.where ? (
                      <span className="quiet friend-where">{one.where}</span>
                    ) : null}

                    {/* Only worth offering when they are here: a message to
                      somebody who has gone is a message nobody receives. */}
                    {one.online ? (
                      <button
                        type="button"
                        title={`Message ${one.name}`}
                        onClick={() => onMessage(one.name)}
                      >
                        Message
                      </button>
                    ) : (
                      <span className="quiet friend-away">Away</span>
                    )}

                    <button
                      type="button"
                      title={`Take ${one.name} off the list`}
                      onClick={() => onAct(FriendAct.RemoveFriend, one.name)}
                    >
                      Remove
                    </button>
                  </div>
                ))
              : showing === 'ignored'
                ? ignored.map((name) => (
                    <div className="friend" key={name}>
                      <span className="friend-name">{name}</span>
                      <button
                        type="button"
                        title={`Hear from ${name} again`}
                        onClick={() => onAct(FriendAct.StopIgnoring, name)}
                      >
                        Remove
                      </button>
                    </div>
                  ))
                : [
                    /* Waiting on an answer from this side first: those are the ones
                     with something to do about them. */
                    ...asking.map((name) => (
                      <div className="friend asked" key={`asking:${name}`}>
                        <span className="friend-name">{name}</span>
                        <span className="quiet friend-where">asked you</span>
                        <button
                          type="button"
                          title={`Add ${name} to your friends list`}
                          onClick={() => onAct(FriendAct.AcceptFriend, name)}
                        >
                          Accept
                        </button>
                        <button
                          type="button"
                          title={`Turn ${name} down`}
                          onClick={() => onAct(FriendAct.DenyFriend, name)}
                        >
                          Decline
                        </button>
                      </div>
                    )),
                    ...asked.map((name) => (
                      <div className="friend" key={`asked:${name}`}>
                        <span className="friend-name">{name}</span>
                        <span className="quiet friend-where">waiting</span>
                        <button
                          type="button"
                          title={`Take back your request to ${name}`}
                          onClick={() => onAct(FriendAct.UnaskFriend, name)}
                        >
                          Cancel
                        </button>
                      </div>
                    )),
                  ]}

            {list.length === 0 ? (
              <p className="hint">
                {showing === 'friends'
                  ? 'Nobody yet. Ask somebody, and once they agree you will be told when they log in.'
                  : showing === 'ignored'
                    ? 'Nobody. Anybody written down here is not heard at all.'
                    : 'Nothing waiting. Ask somebody below and they will be asked to agree.'}
              </p>
            ) : null}
          </div>

          <form className="friends-add" onSubmit={add}>
            <input
              type="text"
              value={typed}
              maxLength={20}
              placeholder={
                showing === 'ignored' ? 'Ignore somebody' : 'Ask somebody to be a friend'
              }
              onChange={(event) => setTyped(event.target.value)}
            />
            <button type="submit" disabled={list.length >= LIMIT}>
              Add
            </button>
          </form>
        </>
      ) : null}
    </div>
  );
}
